import { Box } from '@mui/material'
import { CalIc, CAL_STATUS, calStatusOf, calFmtT, calRangeT, calInitials } from './calShared'

/**
 * Desktop calendar appointment card + hover peek + cluster card + month chip,
 * per the Claude Design "Appointment card · anatomy & states" and
 * "Overlap redesign · clustered columns" boards.
 *
 * The card is colored by the visit's service color, self-adapts to its slot
 * height (xl / lg / md / sm density) and column width (container queries),
 * and carries a pure-CSS hover peek with the full details.
 */

const TEXT = '#2E2A40'
const MUTED = '#8B85A4'
const FAINT = '#F0EDF7'

// ── hover peek building blocks (design pk-* / mp-* anatomy) ─────────
const durChipSx = {
	flex: 'none', fontSize: '10.5px', fontWeight: 700, color: MUTED, bgcolor: '#F4F1FA',
	borderRadius: '5px', p: '2px 6px', fontVariantNumeric: 'tabular-nums',
}

// mp-line: label stretches, amount stays put. The stretch lives on the label
// styles themselves (lineLabelSx) — NOT on a `> :first-of-type` rule: with
// mixed children (<strong> + <span>) that pseudo-class also matches the first
// span, giving the amount flex:1 and un-right-aligning the price.
const lineSx = { display: 'flex', alignItems: 'baseline', gap: '8px', minWidth: 0 }
const lineLabelSx = { flex: 1, minWidth: 0 }
const amtSx = { flex: 'none', fontSize: '11.5px', fontWeight: 750, color: '#5C5578', fontVariantNumeric: 'tabular-nums' }
const amtSubSx = { ...amtSx, fontSize: '10.8px', fontWeight: 650, color: '#9A93B4' }
const nameSx = {
	...lineLabelSx,
	fontSize: '12.5px', fontWeight: 750, color: TEXT, whiteSpace: 'nowrap',
	overflow: 'hidden', textOverflow: 'ellipsis',
	'& em': { fontStyle: 'normal', fontWeight: 600, color: MUTED, fontSize: '11px' },
}
const pawAvSx = (colorSet) => ({
	width: 30, height: 30, flex: 'none', borderRadius: '50%', display: 'flex',
	alignItems: 'center', justifyContent: 'center', bgcolor: colorSet.soft, color: colorSet.deep,
})

// add-on line: "+ Name …… $price"
const PkAddLine = ({ add, accent }) => (
	<Box sx={lineSx}>
		<Box component="span" sx={{ ...lineLabelSx, display: 'flex', alignItems: 'center', gap: '4px', fontSize: '10.8px', fontWeight: 650, color: MUTED }}>
			<Box component="i" sx={{ fontStyle: 'normal', fontWeight: 800, color: accent, flex: 'none' }}>+</Box>
			<Box component="span" sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{add.name}</Box>
		</Box>
		{add.priceLabel && <Box component="span" sx={amtSubSx}>{add.priceLabel}</Box>}
	</Box>
)

const PkStatusPill = ({ st }) => (
	<Box component="span" sx={{
		flex: 'none', height: 21, px: '9px', borderRadius: '99px', fontSize: '10.5px',
		fontWeight: 750, display: 'inline-flex', alignItems: 'center', bgcolor: st.bg, color: st.fg,
	}}>
		{st.label}
	</Box>
)

const PkTimeRow = ({ visit }) => (
	<Box sx={{ display: 'flex', alignItems: 'center', gap: '5px', mt: '6px', fontSize: '11.5px', fontWeight: 650, color: MUTED }}>
		<CalIc name="clock" size={13} sw={1.7} />
		<span>{calRangeT(visit.start, visit.end)}</span>
		<Box component="em" sx={{ fontStyle: 'normal', color: '#C4BDD6' }}>·</Box>
		<span>{visit.start.format('MMM D')}</span>
		<Box component="span" sx={{ ...durChipSx, ml: 'auto' }}>{`${visit.mins} min`}</Box>
	</Box>
)

const PkClientRow = ({ client }) => (
	<Box sx={{ display: 'flex', alignItems: 'center', gap: '5px', mt: '5px', fontSize: '11.5px', fontWeight: 700, color: '#5C5578', '& svg': { color: '#A9A2C0' } }}>
		<CalIc name="user" size={12} sw={1.8} />
		<span>{client}</span>
	</Box>
)

const PkFoot = ({ visit }) => (
	<Box sx={{
		display: 'flex', alignItems: 'center', justifyContent: 'space-between',
		mt: '10px', pt: '9px', borderTop: `1px solid ${FAINT}`,
	}}>
		<Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11.5px', fontWeight: 700, color: '#5C5578' }}>
			{visit.employee && (
				<>
					<Box component="span" sx={{
						width: 19, height: 19, borderRadius: '50%', bgcolor: '#F0EEF6', color: '#6E6688',
						fontSize: '8.5px', fontWeight: 800, display: 'inline-flex', alignItems: 'center',
						justifyContent: 'center', flex: 'none', letterSpacing: '0.02em',
					}}>
						{calInitials(visit.employee)}
					</Box>
					{`with ${visit.employee.split(' ')[0]}`}
				</>
			)}
		</Box>
		<Box sx={{ display: 'flex', alignItems: 'baseline', gap: '6px' }}>
			<Box component="span" sx={{ fontSize: '10.5px', fontWeight: 700, color: MUTED, letterSpacing: '0.02em', textTransform: 'uppercase' }}>
				Total
			</Box>
			<Box component="strong" sx={{ fontSize: '14.5px', fontWeight: 800, color: TEXT }}>
				{visit.priceLabel}
			</Box>
		</Box>
	</Box>
)

// ── hover peek body (shared by card / month chip / cluster) ─────────
export function CalPeekBody({ visit, now }) {
	const st = CAL_STATUS[calStatusOf(visit, now)]

	if (visit.multi) {
		const names = visit.pets.map(p => p.pet)
		const title = names.length > 1
			? `${names.slice(0, -1).join(', ')} & ${names[names.length - 1]}`
			: names[0]
		return (
			<>
				<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
					<Box component="span" sx={{
						fontSize: '13.5px', fontWeight: 800, letterSpacing: '-0.01em', color: TEXT,
						minWidth: 0, whiteSpace: 'normal', textWrap: 'pretty',
					}}>
						{title}
					</Box>
					<PkStatusPill st={st} />
				</Box>
				<PkTimeRow visit={visit} />
				<PkClientRow client={visit.client} />
				<Box sx={{ height: '1px', bgcolor: FAINT, my: '10px' }} />
				<Box sx={{ display: 'flex', flexDirection: 'column' }}>
					{visit.pets.map((p, i) => (
						<Box key={i} sx={{
							display: 'flex', alignItems: 'flex-start', gap: '9px', py: '11px', minWidth: 0,
							'&:first-of-type': { pt: 0 }, '&:last-of-type': { pb: 0 },
							borderTop: i > 0 ? '1px solid #F5F2FA' : 'none',
						}}>
							<Box sx={pawAvSx(p.colorSet)}>
								<CalIc name="paw" size={14} sw={1.4} />
							</Box>
							<Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '4px' }}>
								<Box sx={lineSx}>
									<Box component="strong" sx={nameSx}>
										{p.pet} {p.breed ? <em>({p.breed})</em> : null}
									</Box>
									<Box component="span" sx={durChipSx}>{`${p.mins} min`}</Box>
								</Box>
								<Box sx={lineSx}>
									<Box component="span" sx={{ ...lineLabelSx, fontSize: '11.5px', fontWeight: 700, color: '#5C5578', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
										{p.svc}
									</Box>
									<Box component="span" sx={amtSx}>{p.svcPriceLabel}</Box>
								</Box>
								{p.adds.map((ad, j) => (
									<PkAddLine key={j} add={ad} accent={p.colorSet.accent} />
								))}
							</Box>
						</Box>
					))}
				</Box>
				<PkFoot visit={visit} />
			</>
		)
	}

	return (
		<>
			<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
				<Box sx={pawAvSx(visit.colorSet)}>
					<CalIc name="paw" size={14} sw={1.4} />
				</Box>
				<Box component="span" sx={{
					flex: 1, minWidth: 0, fontSize: '13.5px', fontWeight: 800, letterSpacing: '-0.01em', color: TEXT,
					'& em': { fontStyle: 'normal', fontWeight: 600, color: MUTED, fontSize: '11.5px', letterSpacing: 0 },
				}}>
					{visit.pet} {visit.breed ? <em>({visit.breed})</em> : null}
				</Box>
				<PkStatusPill st={st} />
			</Box>
			<PkTimeRow visit={visit} />
			<PkClientRow client={visit.client} />
			<Box sx={{ height: '1px', bgcolor: FAINT, my: '10px' }} />
			<Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
				<Box sx={lineSx}>
					<Box component="strong" sx={nameSx}>{visit.svcName}</Box>
					<Box component="span" sx={amtSx}>{visit.pets[0].svcPriceLabel}</Box>
				</Box>
				{visit.adds.map((ad, j) => (
					<PkAddLine key={j} add={ad} accent={visit.colorSet.accent} />
				))}
			</Box>
			<PkFoot visit={visit} />
		</>
	)
}

// Design `.cal-peek` is 238px, sized around the mock's short demo strings;
// real service/client names ellipsize hard at that width (reported as "the
// hover card looks narrow"), so we run 26px wider — the same width the
// design's own anatomy board uses to present the peek.
const peekBaseSx = {
	position: 'absolute', top: 0, width: 264, zIndex: 70, opacity: 0, pointerEvents: 'none',
	transition: 'opacity 0.15s ease 0.08s', bgcolor: '#FFFFFF', border: '1px solid #E7E2F3',
	borderRadius: '13px', boxShadow: '0 14px 34px rgba(46, 42, 64, 0.18)', p: '12px 13px 11px',
	cursor: 'default', textAlign: 'left', fontWeight: 400,
}

const peekPosSx = (side, vAlign) => ({
	...(side === 'left' ? { right: 'calc(100% + 10px)', left: 'auto' } : { left: 'calc(100% + 10px)' }),
	...(vAlign === 'bottom' ? { top: 'auto', bottom: 0 } : {}),
})

// ── appointment card ────────────────────────────────────────────────
// density by height: xl ≥100 · lg ≥58 · md ≥34 · sm below (single line)
export function CalApptCard({ visit, now, style, side = 'right', onClick, noPeek = false }) {
	const st = calStatusOf(visit, now)
	const h = (style && style.height) || 84
	const d = h >= 100 ? 'xl' : h >= 58 ? 'lg' : h >= 34 ? 'md' : 'sm'
	const noshow = st === 'noshow'
	const multi = visit.multi && !noshow
	const vAlign = visit.sm >= 14.5 * 60 ? 'bottom' : 'top'
	const c = visit.colorSet
	// no-show reads as "the slot was left empty" (design st-noshow): hollow white
	// card, soft red outline, struck pet name, muted body, one explicit red label.
	const accent = noshow ? '#CF6F69' : c.accent
	const deep = noshow ? '#8B85A4' : c.deep
	const maxRows = d === 'xl' ? 3 : 2

	const statusIc =
		st === 'in' ? (
			<Box component="span" title="In progress" sx={{
				width: 7, height: 7, borderRadius: '50%', bgcolor: '#2F8A5B', flex: 'none',
				animation: 'calPulse 2s infinite',
				'@keyframes calPulse': {
					'0%': { boxShadow: '0 0 0 0 rgba(47, 138, 91, 0.4)' },
					'70%': { boxShadow: '0 0 0 5px rgba(47, 138, 91, 0)' },
					'100%': { boxShadow: '0 0 0 0 rgba(47, 138, 91, 0)' },
				},
				'@media (prefers-reduced-motion: reduce)': { animation: 'none' },
			}} />
		) : st === 'done' ? (
			<Box component="span" title="Completed" sx={{
				width: 13, height: 13, borderRadius: '50%', bgcolor: 'rgba(46, 42, 64, 0.12)', color: '#5C5578',
				display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 'none',
			}}>
				<CalIc name="check" size={9} sw={2.2} />
			</Box>
		) : noshow ? (
			<Box component="span" className="ca-nstag" title="No-show" sx={{
				display: 'inline-flex', alignItems: 'center', gap: '3px', height: d === 'sm' ? 15 : 16,
				p: '0 6px 0 5px', borderRadius: '99px', bgcolor: '#FBECEB', color: '#A53B36',
				fontSize: '9.5px', fontWeight: 800, letterSpacing: '0.02em', whiteSpace: 'nowrap',
				flex: 'none', lineHeight: 1,
			}}>
				<CalIc name="ban" size={10} sw={1.9} />
				<span>No-show</span>
			</Box>
		) : null

	const badge = multi ? (
		<Box component="span" title={`${visit.pets.length} pets, one visit`} sx={{
			display: 'inline-flex', alignItems: 'center', gap: '3px', height: 15, px: '5.5px',
			borderRadius: '99px', bgcolor: '#EDE9FB', color: '#5F4EC2', fontSize: '9.5px', fontWeight: 800, flex: 'none',
		}}>
			<CalIc name="paw" size={9} sw={1.7} />
			{visit.pets.length}
		</Box>
	) : null

	const timeSx = {
		display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '5px',
		fontSize: '10.5px', fontWeight: 750, color: deep, letterSpacing: '0.01em',
		whiteSpace: 'nowrap', minWidth: 0,
		'& > span:first-of-type': { overflow: 'hidden', textOverflow: 'ellipsis' },
	}

	// "+N" add-on count chip (design ca-addn) — the card only counts; the
	// hover peek lists each add-on by name.
	const addnSx = {
		flex: 'none', fontSize: '9.5px', fontWeight: 800, color: deep,
		bgcolor: 'rgba(46, 42, 64, 0.07)', borderRadius: '4px', p: '1px 4px',
		lineHeight: 1.35, fontVariantNumeric: 'tabular-nums',
	}
	const addTitle = (adds) => `Add-ons: ${adds.map(a => a.name).join(', ')}`

	return (
		<Box
			onClick={onClick}
			className={`ca-d-${d}`}
			data-calcard="1"
			sx={{
				position: 'absolute', borderRadius: '9px', overflow: 'hidden', cursor: 'pointer',
				containerType: 'inline-size',
				transition: 'transform 0.12s ease, box-shadow 0.12s ease, opacity 0.12s ease',
				boxShadow: noshow ? 'inset 0 0 0 1.5px #EDCCC9, 0 1px 2px rgba(46, 42, 64, 0.03)' : '0 1px 2px rgba(46, 42, 64, 0.05)',
				fontFamily: "'Outfit', sans-serif",
				bgcolor: noshow ? '#FFFFFF' : multi ? '#FFFFFF' : c.soft,
				...(multi
					? { border: '1px solid #EAE6F4', pl: '13px', pr: '8px', pt: '5px', pb: '4px' }
					: { borderLeft: `3px solid ${accent}`, p: '5px 8px 4px 9px' }),
				...(d === 'sm' ? { display: 'flex', alignItems: 'center', pt: '1px', pb: 0 } : {}),
				...(st === 'done' ? { opacity: 0.55 } : {}),
				'&:hover': {
					transform: 'translateY(-1px)',
					boxShadow: noshow ? 'inset 0 0 0 1.5px #E3B5B1, 0 8px 20px rgba(46, 42, 64, 0.14)' : '0 8px 20px rgba(46, 42, 64, 0.16)',
					overflow: 'visible', zIndex: '46 !important', opacity: 1,
					'& .cal-peek': { opacity: 1 },
				},
				...(noshow ? {
					'& .ca-pet, & .ca-pet-s': {
						color: '#6E6886', textDecoration: 'line-through',
						textDecorationColor: '#D08F8A', textDecorationThickness: '1.5px',
					},
					'& .ca-breed, & .ca-svcs': { textDecoration: 'none', display: 'inline-block' },
					'& .ca-svc, & .ca-price, & .ca-add, & .ca-groomer': { color: '#A39CB8' },
					'& .ca-addn': { color: '#A39CB8', bgcolor: '#F3F0F8' },
				} : {}),
				// width-adaptive density (narrow lanes drop detail)
				'@container (max-width: 132px)': {
					'& .ca-breed': { display: 'none' },
					'& .ca-nstag span': { display: 'none' },
					'& .ca-nstag': { p: '0 4px' },
				},
				'@container (max-width: 110px)': {
					'& .ca-price, & .ca-psvc, & .ca-pprice, & .ca-svcs': { display: 'none' },
				},
				'@container (max-width: 86px)': {
					'& .ca-svc, & .ca-groomer, & .ca-add': { display: 'none' },
				},
				...style,
			}}
		>
			{multi && (
				<Box sx={{
					position: 'absolute', left: 0, top: 0, bottom: 0, width: 4, display: 'flex',
					flexDirection: 'column', borderRadius: '8px 0 0 8px', overflow: 'hidden',
				}}>
					{visit.pets.map((p, i) => (
						<Box key={i} sx={{ flex: 1, bgcolor: p.colorSet.accent }} />
					))}
				</Box>
			)}
			{d === 'sm' ? (
				<Box sx={{ display: 'flex', alignItems: 'center', gap: '5px', minWidth: 0, width: '100%' }}>
					<Box component="span" sx={{ ...timeSx, flex: 'none' }}>{calFmtT(visit.start, false)}</Box>
					<Box component="span" className="ca-pet-s" sx={{
						fontSize: '11px', fontWeight: 750, color: TEXT, whiteSpace: 'nowrap',
						overflow: 'hidden', textOverflow: 'ellipsis', flex: 1, minWidth: 0,
					}}>
						{visit.pet}
						{!multi && (
							<Box component="span" className="ca-svcs" sx={{ fontWeight: 600, color: '#7A7492' }}>
								{` · ${visit.svcName}`}
							</Box>
						)}
					</Box>
					{badge}
					{statusIc}
				</Box>
			) : (
				<>
					{/* Every density above `sm` shows the full range, so short visits
					    (30 / 40 min) carry an end time just like the longer ones. */}
					<Box sx={timeSx}>
						<span>{calRangeT(visit.start, visit.end)}</span>
						<Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: '4px', flex: 'none' }}>
							{badge}
							{statusIc}
						</Box>
					</Box>
					{multi && d !== 'md' ? (
						<>
							{visit.pets.slice(0, maxRows).map((p, i) => (
								<Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: '6px', mt: '3px', minWidth: 0 }}>
									<Box sx={{ width: 7, height: 7, borderRadius: '50%', flex: 'none', bgcolor: p.colorSet.accent }} />
									<Box component="span" sx={{
										fontSize: '11.5px', fontWeight: 750, color: TEXT, whiteSpace: 'nowrap',
										overflow: 'hidden', textOverflow: 'ellipsis', letterSpacing: '-0.01em',
									}}>
										{p.pet}
									</Box>
									<Box component="span" className="ca-psvc" sx={{
										fontSize: '10.5px', fontWeight: 600, color: '#7A7492', whiteSpace: 'nowrap',
										overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0,
									}}>
										{p.svc}
									</Box>
									{p.adds.length > 0 && (
										<Box component="span" title={addTitle(p.adds)} sx={addnSx}>
											{`+${p.adds.length}`}
										</Box>
									)}
									{d === 'xl' && (
										<Box component="span" className="ca-pprice" sx={{ ml: 'auto', fontSize: '10.5px', fontWeight: 750, color: '#5C5578', flex: 'none' }}>
											{p.priceLabel}
										</Box>
									)}
								</Box>
							))}
							{visit.pets.length > maxRows && (
								<Box sx={{ fontSize: '10px', fontWeight: 700, color: MUTED, mt: '2px' }}>
									{`+${visit.pets.length - maxRows} more`}
								</Box>
							)}
							{d === 'xl' && (
								<Box className="ca-groomer" sx={{ display: 'flex', alignItems: 'center', gap: '5px', mt: '4px', fontSize: '10.5px', fontWeight: 700, color: '#6E6688' }}>
									<CalIc name="user" size={10} sw={1.8} />
									<span>{visit.employee ? `with ${visit.employee.split(' ')[0]}` : 'Unassigned'}</span>
									<Box component="span" className="ca-pprice" sx={{ ml: 'auto', fontSize: '11px', fontWeight: 750, color: '#3A3550', flex: 'none' }}>
										{visit.priceLabel}
									</Box>
								</Box>
							)}
						</>
					) : (
						<>
							<Box className="ca-pet" sx={{
								fontSize: '12.5px', fontWeight: 750, color: TEXT, whiteSpace: 'nowrap',
								overflow: 'hidden', textOverflow: 'ellipsis', mt: '1px', letterSpacing: '-0.01em',
							}}>
								{visit.pet}
								{!multi && visit.breed && (
									<Box component="span" className="ca-breed" sx={{ fontWeight: 600, color: '#7A7492', fontSize: '11px' }}>
										{` (${visit.breed})`}
									</Box>
								)}
							</Box>
							{d !== 'md' && !multi && (
								<Box className="ca-svc" sx={{
									display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '6px',
									fontSize: '11px', fontWeight: 650, color: '#5C5578', mt: '1px', minWidth: 0,
								}}>
									<Box component="span" sx={{ flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
										{visit.svcName}
									</Box>
									{d !== 'xl' && visit.adds.length > 0 && (
										<Box component="span" title={addTitle(visit.adds)} sx={addnSx}>
											{`+${visit.adds.length}`}
										</Box>
									)}
									{d !== 'xl' && (
										<Box component="span" className="ca-price" sx={{ fontWeight: 750, color: deep, flex: 'none' }}>
											{visit.priceLabel}
										</Box>
									)}
								</Box>
							)}
							{d === 'xl' && !multi && visit.adds.map((ad, j) => (
								<Box className="ca-add" key={j} title={`Add-on: ${ad.name}`} sx={{
									display: 'flex', alignItems: 'center', gap: '4px', mt: j === 0 ? '2px' : '1px',
									fontSize: '10.5px', fontWeight: 650, color: '#6E6688', minWidth: 0,
								}}>
									<Box component="i" sx={{ fontStyle: 'normal', fontWeight: 800, color: deep, flex: 'none' }}>+</Box>
									<Box component="span" sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{ad.name}</Box>
								</Box>
							))}
							{d === 'xl' && !multi && (
								<Box className="ca-groomer" sx={{ display: 'flex', alignItems: 'center', gap: '5px', mt: '4px', fontSize: '10.5px', fontWeight: 700, color: '#6E6688', minWidth: 0, whiteSpace: 'nowrap' }}>
									<CalIc name="user" size={10} sw={1.8} />
									<Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
										{visit.employee ? `with ${visit.employee.split(' ')[0]}` : 'Unassigned'}
									</Box>
									<Box component="span" className="ca-price" sx={{ ml: 'auto', fontSize: '11px', fontWeight: 750, color: '#3A3550', flex: 'none' }}>
										{visit.priceLabel}
									</Box>
								</Box>
							)}
						</>
					)}
				</>
			)}
			{!noPeek && (
				<Box className="cal-peek" sx={{ ...peekBaseSx, ...peekPosSx(side, vAlign) }}>
					<CalPeekBody visit={visit} now={now} />
				</Box>
			)}
		</Box>
	)
}

// ── cluster card (overlap redesign) ─────────────────────────────────
export function CalClusterPeekBody({ items, start, end }) {
	const sorted = [...items].sort((a, b) => a.sm - b.sm)
	return (
		<>
			<Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: '8px' }}>
				<Box component="strong" sx={{ fontSize: '13px', fontWeight: 800, letterSpacing: '-0.01em', color: TEXT }}>
					{`${items.length} appointments`}
				</Box>
				<Box component="span" sx={{ fontSize: '11px', fontWeight: 650, color: MUTED, whiteSpace: 'nowrap' }}>
					{calRangeT(start, end)}
				</Box>
			</Box>
			<Box sx={{ mt: '7px', display: 'flex', flexDirection: 'column' }}>
				{sorted.map(v => (
					<Box key={v.key} sx={{ display: 'flex', alignItems: 'center', gap: '8px', py: '7px', borderTop: `1px solid ${FAINT}`, minWidth: 0 }}>
						<Box sx={{ width: 3, height: 26, borderRadius: '2px', flex: 'none', bgcolor: v.colorSet.accent }} />
						<Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '1px' }}>
							<Box component="strong" sx={{ fontSize: '12px', fontWeight: 750, color: TEXT, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
								{v.pet}
							</Box>
							<Box component="span" sx={{ fontSize: '10.5px', fontWeight: 600, color: MUTED, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
								{v.svcName}
								{v.employee ? ` · with ${v.employee.split(' ')[0]}` : ''}
							</Box>
						</Box>
						<Box sx={{ flex: 'none', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '1px' }}>
							<Box component="strong" sx={{ fontSize: '11px', fontWeight: 750, color: '#5C5578' }}>
								{calFmtT(v.start, true)}
							</Box>
							<Box component="span" sx={{ fontSize: '10.5px', fontWeight: 650, color: MUTED }}>
								{v.priceLabel}
							</Box>
						</Box>
					</Box>
				))}
			</Box>
			<Box sx={{
				display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px',
				mt: '6px', pt: '8px', borderTop: `1px solid ${FAINT}`,
				fontSize: '11px', fontWeight: 750, color: '#5F4EC2',
			}}>
				<span>Click to open day view</span>
				<CalIc name="chevR" size={11} sw={2} />
			</Box>
		</>
	)
}

export function CalClusterCard({ items, start, end, style, side = 'right', onOpen, noPeek = false }) {
	const allAccents = items.flatMap(v => v.multi ? v.pets.map(p => p.colorSet.accent) : [v.colorSet.accent])
	const distinct = [...new Set(allAccents)]
	const h = (style && style.height) || 84
	const d = h >= 74 ? 'lg' : h >= 40 ? 'md' : 'sm'
	const sm = Math.min(...items.map(v => v.sm))
	const vBottom = sm >= 14.5 * 60

	return (
		<Box
			onClick={onOpen}
			data-calcard="1"
			sx={{
				position: 'absolute', bgcolor: '#FFFFFF', border: '1px solid #EAE6F4',
				borderRadius: '9px', overflow: 'hidden', cursor: 'pointer',
				containerType: 'inline-size', fontFamily: "'Outfit', sans-serif",
				transition: 'transform 0.12s ease, box-shadow 0.12s ease',
				boxShadow: '0 1px 2px rgba(46, 42, 64, 0.05)',
				...(d === 'sm'
					? { display: 'flex', alignItems: 'center', pt: '1px', pb: 0, pl: '11px', pr: '7px' }
					: { p: '5px 8px 4px 12px' }),
				'&:hover': {
					transform: 'translateY(-1px)', boxShadow: '0 8px 20px rgba(46, 42, 64, 0.16)',
					borderColor: '#C9BEEC', overflow: 'visible', zIndex: '46 !important',
					'& .cal-peek': { opacity: 1 },
				},
				'@container (max-width: 118px)': { '& .cc-gnames': { display: 'none' } },
				...style,
			}}
		>
			<Box sx={{
				position: 'absolute', left: 0, top: 0, bottom: 0, width: 4, display: 'flex',
				flexDirection: 'column', borderRadius: '8px 0 0 8px', overflow: 'hidden',
			}}>
				{allAccents.map((accent, i) => (
					<Box key={i} sx={{ flex: 1, bgcolor: accent }} />
				))}
			</Box>
			{d === 'sm' ? (
				<Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', minWidth: 0, width: '100%' }}>
					<Box component="span" sx={numPillSx}>{items.length}</Box>
					<Box component="span" sx={{ fontSize: '10.5px', fontWeight: 650, color: MUTED, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
						{calRangeT(start, end)}
					</Box>
				</Box>
			) : (
				<>
					<Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', fontWeight: 750, color: TEXT, whiteSpace: 'nowrap', overflow: 'hidden' }}>
						<Box component="span" sx={numPillSx}>{items.length}</Box>
						appointments
					</Box>
					<Box sx={{ fontSize: '10.5px', fontWeight: 650, color: MUTED, mt: '2px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
						{calRangeT(start, end)}
					</Box>
					{d === 'lg' && (
						<Box sx={{ display: 'flex', alignItems: 'center', mt: '5px', minWidth: 0 }}>
							{distinct.map((accent, i) => (
								<Box key={accent} sx={{
									width: 12, height: 12, borderRadius: '50%', border: '2px solid #FFFFFF',
									flex: 'none', bgcolor: accent, ml: i > 0 ? '-4px' : 0,
								}} />
							))}
							<Box component="span" className="cc-gnames" sx={{
								ml: '6px', fontSize: '10px', fontWeight: 650, color: MUTED,
								whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
							}}>
								{`${items.length} bookings`}
							</Box>
						</Box>
					)}
				</>
			)}
			{!noPeek && (
				<Box className="cal-peek" sx={{
					// design `.cal-peek.ccp` is 258 — widened in step with the
					// base peek (see peekBaseSx).
					...peekBaseSx, width: 284,
					...peekPosSx(side, vBottom ? 'bottom' : 'top'),
				}}>
					<CalClusterPeekBody items={items} start={start} end={end} />
				</Box>
			)}
		</Box>
	)
}

const numPillSx = {
	minWidth: 17, height: 17, flex: 'none', borderRadius: '99px', bgcolor: '#EDE9FB',
	color: '#5F4EC2', fontSize: '10px', fontWeight: 800, display: 'inline-flex',
	alignItems: 'center', justifyContent: 'center', px: '4px',
}

// ── month chip ──────────────────────────────────────────────────────
export function CalMonthChip({ visit, now, onClick, side = 'under', hAlign = 'left', noPeek = false }) {
	const noshow = calStatusOf(visit, now) === 'noshow'
	const multi = visit.multi
	const label = multi ? `${visit.pets[0].pet} +${visit.pets.length - 1}` : visit.pet
	const distinct = multi ? [...new Set(visit.pets.map(p => p.colorSet.accent))] : []

	return (
		<Box
			onClick={onClick}
			sx={{
				height: 21, flex: 'none', borderRadius: '6px', display: 'flex', alignItems: 'center',
				gap: '5px', px: '7px', cursor: 'pointer', position: 'relative', minWidth: 0,
				fontFamily: "'Outfit', sans-serif",
				bgcolor: noshow ? '#FFFFFF' : visit.colorSet.soft,
				color: noshow ? '#8B85A4' : visit.colorSet.deep,
				...(noshow ? {
					boxShadow: 'inset 0 0 0 1px #EDCCC9',
					'& .mc-pet': { textDecoration: 'line-through', textDecorationColor: '#D08F8A' },
				} : {}),
				'&:hover': {
					filter: 'brightness(0.965) saturate(1.1)', zIndex: 46,
					'& .cal-peek': { opacity: 1 },
				},
			}}
		>
			{multi && !noshow ? (
				<Box sx={{ display: 'flex', flex: 'none' }}>
					{distinct.map((accent, i) => (
						<Box key={accent} sx={{
							width: 8, height: 8, borderRadius: '50%', border: '1.5px solid #FFFFFF',
							bgcolor: accent, ml: i > 0 ? '-3px' : 0, flex: 'none',
						}} />
					))}
				</Box>
			) : (
				<Box sx={{ width: 6, height: 6, borderRadius: '50%', flex: 'none', bgcolor: noshow ? '#CF6F69' : visit.colorSet.accent }} />
			)}
			<Box component="span" className="mc-pet" sx={{
				fontSize: '10.8px', fontWeight: 750, whiteSpace: 'nowrap', overflow: 'hidden',
				textOverflow: 'ellipsis', minWidth: '3ch',
			}}>
				{label}
			</Box>
			<Box component="span" sx={{
				ml: 'auto', flex: '0 1 auto', minWidth: 0, overflow: 'hidden',
				fontSize: '10px', fontWeight: 650, opacity: 0.75, whiteSpace: 'nowrap',
			}}>
				{calFmtT(visit.start, true)}
			</Box>
			{!noPeek && (
				<Box className="cal-peek" sx={{
					...peekBaseSx,
					...(side === 'above'
						? { bottom: 'calc(100% + 6px)', top: 'auto' }
						: { top: 'calc(100% + 6px)' }),
					...(hAlign === 'right' ? { left: 'auto', right: 0 } : { left: 0 }),
				}}>
					<CalPeekBody visit={visit} now={now} />
				</Box>
			)}
		</Box>
	)
}
